import React, { Component } from "react";

import { TreeView, Checkbox } from "~/core";
import { IRecordObject } from "~/core/components/tree-view/tree-view";

interface ICategoryTreeProps {
    defaultNoMatch?: boolean;
    formatMessage?: (id: string) => string;
    onStatusChange?: (categories: Record<string, any>) => void;
    categories?: Record<string, any>;
    categoryDataSet?: IRecordObject[];
}

interface ICategoryTreeState {
    categories: Record<string, any>;
    dataSet: IRecordObject[];
}

export class CategoryTree extends Component<ICategoryTreeProps, ICategoryTreeState> {
    static defaultProps = {
        defaultNoMatch: false,
    };

    constructor(props: ICategoryTreeProps) {
        super(props);
        const { categories, categoryDataSet } = props;
        for (const cat of Object.keys(categories)) {
            if (!this._dataSetContainsCategory(categoryDataSet, cat)) {
                categories[cat] = props.defaultNoMatch;
            }
        }
        this.state = {
            categories,
            dataSet: categoryDataSet,
        };
    }

    _dataSetContainsCategory(dataSet, catId) {
        if (dataSet == null || catId == null) {
            return false;
        }
        if (dataSet.id === catId) {
            return true;
        }
        if (Array.isArray(dataSet) || Array.isArray(dataSet.children)) {
            const dSet = Array.isArray(dataSet) ? dataSet : dataSet.children;
            for (const data of dSet) {
                if (this._dataSetContainsCategory(data, catId)) {
                    return true;
                }
            }
        }
        return false;
    }

    _setDescendants(categories, children, value) {
        (children || []).forEach((child) => {
            categories[child.id] = value;
            this._setDescendants(categories, child.children, value);
        });
    }

    // Ancestors of the node at nodeIndex, nearest first
    _getAncestors(nodeIndex) {
        const ancestors = [];
        let level = this.state.dataSet;
        nodeIndex
            .split(".children.")
            .slice(0, -1)
            .forEach((index) => {
                const ancestor = level[index];
                ancestors.unshift(ancestor);
                level = ancestor.children;
            });
        return ancestors;
    }

    onStatusChange = (node, value) => {
        const { categories } = this.state;
        categories[node.id] = value;
        this._setDescendants(categories, node.children, value);
        this._getAncestors(node.nodeIndex).forEach((ancestor) => {
            if (!categories[ancestor.id]) {
                categories[ancestor.id] = value;
            }
            const noChildSelected = ancestor.children.every((child) => !categories[child.id]);
            if (noChildSelected) {
                categories[ancestor.id] = false;
            }
        });
        this.setState(
            {
                categories,
            },
            () => {
                if (this.props.onStatusChange) {
                    this.props.onStatusChange(this.state.categories);
                }
            }
        );
    };

    // Leaf descendants of a node, as on/off values
    _getLeafValues(node): boolean[] {
        return (node.children || []).flatMap((child) =>
            child.children && child.children.length
                ? this._getLeafValues(child)
                : [!!this.state.categories[child.id]]
        );
    }

    _isPartial(node) {
        const leaves = this._getLeafValues(node);
        return leaves.some((on) => on) && leaves.some((on) => !on);
    }

    renderCheckbox = (node) => {
        return (
            <Checkbox
                value={this.state.categories[node.id] || node.partialSelect}
                someChecked={this._isPartial(node)}
                onChange={(evt, value) => this.onStatusChange(node, value)}
                testId={`category-tree-node-checkbox-${node.id}`}
            />
        );
    };

    UNSAFE_componentWillReceiveProps(nextProps) {
        if (JSON.stringify(nextProps.categories) !== JSON.stringify(this.props.categories)) {
            this.setState({
                categories: nextProps.categories,
            });
        }
    }

    render() {
        return (
            <div className="category-tree">
                <TreeView
                    rowRenderer={this.renderCheckbox}
                    data={this.state.dataSet}
                    displayLabel={"name"}
                />
            </div>
        );
    }
}
